import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Check, X, ShieldCheck, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAdmin } from "@/context/AdminContext";
import { toast } from "sonner";

/**
 * Auto-pairing approval page.
 *
 * Third-party app sends the admin to:
 *   /admin/connect
 *     ?pairing_id=<one-time-token>
 *     &app_name=<name>
 *     &redirect_uri=<full url>
 *     &return_url=<full url>
 *     &icon_url=<optional>
 *     &description=<optional>
 *     &permissions=read,write   (comma sep, optional)
 *
 * After admin approval, this page POSTs to /api/admin/connect/approve which
 * creates the ConnectedApp and stages the shared_secret keyed by pairing_id
 * for 2 minutes. We then redirect the browser to:
 *   <return_url>?pairing_id=<same>&status=approved
 *
 * The third-party app's server then exchanges the pairing_id for the
 * shared_secret over server-to-server (POST /api/oauth/exchange-pairing).
 */
export default function AdminConnect() {
  const [params] = useSearchParams();
  const { admin, loading } = useAdmin();
  const navigate = useNavigate();

  const pairing_id = params.get("pairing_id") || "";
  const initialName = params.get("app_name") || "";
  const initialRedirect = params.get("redirect_uri") || "";
  const initialIcon = params.get("icon_url") || "";
  const initialDesc = params.get("description") || "";
  const initialPerms = params.get("permissions") || "";
  const return_url = params.get("return_url") || "";

  const [name, setName] = useState(initialName);
  const [redirectUri, setRedirectUri] = useState(initialRedirect);
  const [iconUrl, setIconUrl] = useState(initialIcon);
  const [description, setDescription] = useState(initialDesc);
  const [permissions, setPermissions] = useState(initialPerms);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const valid = pairing_id.length >= 16 && name && redirectUri && return_url;

  useEffect(() => {
    if (!loading && !admin) {
      const next = `/admin/connect?${params.toString()}`;
      navigate(`/admin/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [loading, admin, navigate, params]);

  if (loading || !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6">
        <Card className="max-w-md bg-zinc-900/60 border-zinc-800 p-6 space-y-3">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-display font-medium">Invalid pairing request</span>
          </div>
          <p className="text-sm text-zinc-400">
            The incoming pairing link is missing required parameters
            (<code className="font-mono text-xs">pairing_id</code>,{" "}
            <code className="font-mono text-xs">app_name</code>,{" "}
            <code className="font-mono text-xs">redirect_uri</code>,{" "}
            <code className="font-mono text-xs">return_url</code>).
          </p>
          <Button asChild variant="outline" className="border-zinc-700"><Link to="/admin">Back to admin</Link></Button>
        </Card>
      </div>
    );
  }

  const finishRedirect = (status) => {
    const sep = return_url.includes("?") ? "&" : "?";
    const url = `${return_url}${sep}pairing_id=${encodeURIComponent(pairing_id)}&status=${status}`;
    window.location.replace(url);
  };

  const approve = async () => {
    setSubmitting(true); setError("");
    try {
      const perms = permissions.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/connect/approve", {
        pairing_id,
        app_name: name,
        redirect_uri: redirectUri,
        icon_url: iconUrl,
        description,
        default_permissions: perms,
      });
      toast.success(`Approved — ${name} is now connected.`);
      finishRedirect("approved");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to approve";
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const reject = () => finishRedirect("rejected");

  const returnHost = (() => { try { return new URL(return_url).host; } catch { return return_url; } })();

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6">
      <Card className="w-full max-w-lg bg-zinc-900/60 border-zinc-800 p-8 space-y-6" data-testid="connect-card">
        <div className="flex items-center justify-between">
          <Logo />
          <div className="text-xs font-mono text-zinc-500">/admin/connect</div>
        </div>

        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-3 text-zinc-300">
            <Logo size={36} />
            <div className="font-display text-xl">↔</div>
            {iconUrl ? (
              <img src={iconUrl} alt="" className="w-9 h-9 rounded-md object-cover border border-zinc-800" />
            ) : (
              <div className="w-9 h-9 rounded-md bg-zinc-800 border border-zinc-700 flex items-center justify-center font-display text-sm">{name?.[0]?.toUpperCase() || "?"}</div>
            )}
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="connect-title">
            Connect {name} to NexusID?
          </h1>
          <p className="text-sm text-zinc-400">
            <span className="font-mono text-xs">{returnHost}</span> is asking to register itself as a connected application.
            Approving will create a new app entry, generate a shared secret, and securely hand it back to <span className="font-mono">{returnHost}</span>.
          </p>
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">App name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800" data-testid="connect-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Sign-in redirect URI</Label>
            <Input value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 font-mono text-xs" data-testid="connect-redirect" />
            <div className="text-[11px] text-zinc-600 mt-1">
              Where users will be redirected after authenticating via NexusID.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Default permissions (optional)</Label>
            <Input value={permissions} onChange={(e) => setPermissions(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800 font-mono text-xs" placeholder="read, write" data-testid="connect-perms" />
          </div>
          {description && (
            <div className="text-xs text-zinc-500"><span className="text-zinc-600">Description:</span> {description}</div>
          )}
        </div>

        <div className="rounded-md border border-emerald-900/40 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>The shared secret will be transmitted server-to-server in a one-time exchange (the browser never sees it). It can be rotated at any time from <span className="font-mono">Admin → Connected Apps</span>.</div>
        </div>

        {error && <div className="text-sm text-red-400 font-mono">{error}</div>}

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reject} className="flex-1 border-zinc-700 text-zinc-300" disabled={submitting} data-testid="connect-reject-btn">
            <X className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button onClick={approve} className="flex-1 bg-white text-zinc-950 hover:bg-zinc-200" disabled={submitting || !valid} data-testid="connect-approve-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Approve &amp; connect</>}
          </Button>
        </div>

        <div className="text-[11px] text-zinc-600 font-mono text-center">
          Pairing ID: {pairing_id.slice(0, 8)}…{pairing_id.slice(-4)} · expires after approval
        </div>
      </Card>
    </div>
  );
}
